import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface FeeStructureItem {
  title: string;
  amount: number;
}

interface FeeStructure {
  id: number;
  title: string;
  icon: string;
  color_code: string;
  fee_structure: FeeStructureItem[] | null;
  sort_order: number;
  status: boolean;
}

export default function FeeStructure() {
  const navigate = useNavigate();
  const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getFeeStructures();
  }, [page, limit, search]);

  const getFeeStructures = async () => {
    setLoading(true);
    const response = await request.post("fee-structures", {
      search: search,
      limit,
      offset: page,
      orderColumn: "sort_order",
      orderDirection: "desc",
    });
    setFeeStructures(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "title",
      label: "Title",
      minWidth: 180,
      format: (value, row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              background: row?.color_code || "#667eea",
            }}
          >
          </Box>
          {value}
        </Box>
      ),
    },
    {
      id: "fee_structure",
      label: "Fee Structure",
      minWidth: 220,
      format: (value) => {
        const items = Array.isArray(value) ? value : [];
        if (items.length === 0) return "—";
        return (
          <Box component="span" sx={{ fontSize: "0.8rem", color: "#475569" }}>
            {items.map((item, idx) => (
              <Box key={idx}>
                {item?.title}: ₹{item?.amount?.toLocaleString?.() ?? item?.amount}
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      id: "sort_order",
      label: "Sort Order",
      minWidth: 110,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/fee-structure/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({
                  open: true,
                  id: row.id,
                  title: row?.title,
                }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Fee Structure"
        showSearch
        searchPlaceholder="Search fee structure..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Fee Structure",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/fee-structure/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={feeStructures}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete Fee Structure - " + deleteDialog?.title}
          message="Are you sure you want to delete this fee structure? This action cannot be undone."
          apiEndpoint={"fee-structures/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getFeeStructures();
          }}
        />
      </PageWrapper>
    </>
  );
}